import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Label,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  Spinner,
  Table,
  Tooltip,
  ModalHeader,
} from 'reactstrap';
import { faInfoCircle, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { isMobile } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePricingBloc } from '../../hooks/usePricing/usePricingBloc';
import Translate, { numStr, t, translateDate } from '../common/Translate';
import Calculate from './Calculate';
import Calculation from './Calculation';
import { formatEntryStocksDays, processDate } from '../../Store/helpers/pricing';
import { currencySign } from '../../Utils/currencySign';
import CounterAppraisalAnalysis from './CounterAppraisalAnalysis';
import VinDecoding from './VinDecoding';
import logoNoa from '../../assets/img/logo_noa-07.svg';
import titleNoa from '../../assets/img/logo_noa-01.svg';

const currentDate = new Date();

const parseNumerator = (str) => {
  const [numerator] = (str?.split('/') || []).map(Number);
  return numerator;
};

const formatGap = (value) => {
  if (value > 0) {
    return { sign: '+', value };
  }
  if (value < 0) {
    return { sign: '-', value: Math.abs(value) };
  }
  return { sign: '', value: 0 };
};

const PricingBloc = ({
  readOnly,
  vinVersions,
  checkedVinVersions,
  versionType,
  versions,
  currentVersion,
  enableVinDecodingForUser,
  versionSupplier,
  versionOrg,
  carEquipment,
}) => {
  const {
    isLoading,
    isOpen,
    toggle,
    modelData,
    message,
    coverage,
    reference,
    permissionFord,
    entryStockOptions,
    carProfile,
    setCarProfile,
    entryStockSelectedKey,
    supervisorEntryDate,
    supervisorEntryStock,
    onChange,
    sourceProfileLabels,
    aiReport,
    automaticNoa,
    handleMonkReport,
    handleMonkReportAuto,
    monkReportStatus,
    monkReportData,
    inspectionVersion,
    tooltipOpenFirst,
    setTooltipOpenFirst,
    tooltipOpenSecond,
    setTooltipOpenSecond,
    monkInspectionVersion,
    originId,
    setVinVersionsOpen,
    vinVersionsOpen,
    counterInspectionVersion,
    counterInspectionMonkVersion,
    caMonkReportData,
    caMonkReportStatus,
    handleMonkReportAutoForCA,
    recordStatus,
    packageNoaMessage,
    modelDataPackageNoa,
    packageNoaLoading,
  } = usePricingBloc();
  const { data } = coverage;
  const [open, setOpen] = useState(false);

  const toggleAccordion = () => {
    setOpen(!open);
  };
  let caAutobizAi = '';
  useEffect(() => {
    if (
      counterInspectionVersion > 0 &&
      counterInspectionVersion > counterInspectionMonkVersion &&
      aiReport &&
      automaticNoa &&
      recordStatus === 40
    ) {
      caAutobizAi = handleMonkReportAutoForCA();
    }
  }, []);
  let autobizAi = '';
  useEffect(() => {
    if (inspectionVersion > 0 && inspectionVersion > monkInspectionVersion && aiReport && automaticNoa) {
      autobizAi = handleMonkReportAuto();
    }
  }, []);
  const classDamages = `${
    Math.abs(monkReportData.autobizDamages - monkReportData.monkDamages) < 3 ? 'damages-green' : 'damages-red'
  } font-weight-bold`;

  const gaps = useMemo(
    () => ({
      monkCompliancesGap: Math.abs(
        parseNumerator(caMonkReportData.monkCompliances) - parseNumerator(monkReportData.monkCompliances),
      ),
      autobizDamagesGap: Math.abs(caMonkReportData.autobizDamages - monkReportData.autobizDamages),
      monkDamagesGap: Math.abs(caMonkReportData.monkDamages - monkReportData.monkDamages),
      autobizFrevosGap: caMonkReportData.autobizFrevos - monkReportData.autobizFrevos,
      monkFrevosGap: caMonkReportData.monkFrevos - monkReportData.monkFrevos,
    }),
    [monkReportData, caMonkReportData],
  );

  const renderFormattedGap = ({ sign, value }) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      {sign === '+' && <span style={{ color: 'red' }}>{sign}</span>}
      {sign === '-' && <span>{sign}</span>}
      <span>{currencySign(value)}</span>
    </span>
  );

  let noaSubTitle = '';
  if (monkReportStatus.status === 'success' && typeof monkReportData === 'object') {
    noaSubTitle = (
      <>
        <span style={{ paddingLeft: 30 }}>
          {t('priceGapNoa')}: {currencySign(Math.abs(monkReportData.autobizFrevos - monkReportData.monkFrevos))}
        </span>
      </>
    );
  } else if (monkReportStatus.status === 'duplicatedData' && typeof monkReportData === 'object') {
    noaSubTitle = (
      <>
        <span className="status-error" style={{ paddingLeft: 30 }}>
          <Translate code="aiReportErrorPhotos" />
        </span>
      </>
    );
  } else if (monkReportStatus.status === 'minimumPictures' && typeof monkReportData === 'object') {
    noaSubTitle = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  } else if (monkReportStatus.status === 'unknowError' && typeof monkReportData === 'object') {
    noaSubTitle = <span className="status-error">{t('apiErrorAiReport')}</span>;
  } else {
    noaSubTitle = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  }

  if (inspectionVersion > 0 && inspectionVersion > monkInspectionVersion && aiReport) {
    if (!automaticNoa) {
      autobizAi = (
        <button
          type="button"
          className="btn btn-block btn-1 mb-1 custom-padding w-auto noa-button"
          onClick={handleMonkReport}
        >
          <img src={logoNoa} alt="logo noa" style={{ marginRight: '8px' }} />
          <span className="d-inline-block d-sm-none d-lg-inline-block px-2">{t('requestAICounterExpertise')} </span>
          {packageNoaLoading && <Spinner color="light" type="grow" size="sm" />}
        </button>
      );
    }
  } else if (monkReportStatus.status === 'pending') {
    autobizAi = <span className="d-inline-block d-sm-none d-lg-inline-block text-waiting">{t('aiReportOngoing')}</span>;
  } else if (monkReportStatus.status === 'success' && typeof monkReportData === 'object') {
    autobizAi = (
      <>
        <span className="trustScore mr-3">
          {t('trustScore')} {monkReportData.monkCompliances}
        </span>
        <FontAwesomeIcon
          icon={faInfoCircle}
          size="1x"
          id="toolTipTrustIndexInformationBox"
          className="info-icon position-absolute ml-n2 mt-1"
          onClick={() => setTooltipOpenFirst((prev) => !prev)}
        />
        <Tooltip
          placement="bottom"
          isOpen={tooltipOpenFirst}
          target="toolTipTrustIndexInformationBox"
          toggle={() => !isMobile && setTooltipOpenFirst((prev) => !prev)}
        >
          {t('trustIndexInformationBox')}
        </Tooltip>
        <Table className="mb-0">
          <thead className="offer-table-header">
            <tr>
              <th>
                <></>
              </th>
              <th className="font-weight-normal border-right border-#dee2e6">{t('buyer')}</th>
              <th className="autobizAi font-weight-normal noa-color">{t('titleAi')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="d-flex">
                  <span className="numberDamagedPart">{t('numberDamagedPart')}</span>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    size="1x"
                    id="toolTipPartCountAIInformationBox"
                    className="info-icon position-absolute"
                    onClick={() => setTooltipOpenSecond((prev) => !prev)}
                  />
                </div>
                <Tooltip
                  placement="bottom"
                  isOpen={tooltipOpenSecond}
                  target="toolTipPartCountAIInformationBox"
                  toggle={() => !isMobile && setTooltipOpenSecond((prev) => !prev)}
                >
                  {t('toolTipPartCountAIInformationBox')}
                </Tooltip>
              </td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{monkReportData.autobizDamages}</td>
              <td className={classDamages}>{monkReportData.monkDamages}</td>
            </tr>
            <tr>
              <td>
                <span className="bodyRefurbishmentPricing">{t('bodyRefurbishmentPricing')}</span>
              </td>
              <td className="font-weight-bold border-right border-#dee2e6 text-lightgray">
                {currencySign(monkReportData.autobizFrevos)}
              </td>
              <td className="font-weight-bold text-lightgray">{currencySign(monkReportData.monkFrevos)}</td>
            </tr>
            <tr>
              <td colSpan={3}>
                <a
                  target="_blank"
                  href={monkReportData.urlReport}
                  className="custom-padding download-btn d-lg-inline noa-color"
                  rel="noopener noreferrer"
                  id="theHrefMonkReport"
                >
                  <span className="d-inline-block d-sm-none d-lg-inline-block noa-color">{t('ctaAiReport')}</span>
                </a>
              </td>
            </tr>
          </tbody>
        </Table>
      </>
    );
  } else if (monkReportStatus.status === 'duplicatedData' && typeof monkReportData === 'object') {
    const duplicationErrors = Object.entries(monkReportStatus.comment);
    autobizAi = (
      <>
        <div>
          <span className="status-error" style={{ paddingLeft: 5 }}>
            <Translate code="aiReportErrorPhotos" />
          </span>
          {duplicationErrors.map((duplicatedImage) => {
            const duplicatedImages = Object.entries(duplicatedImage[1]);
            return (
              <>
                <div className="status-error">
                  &nbsp;
                  <Translate code={duplicatedImage[0]} />
                  &nbsp;
                  <Translate code="photosSimilar" /> : &nbsp;
                  {duplicatedImages.map((img) => (
                    <>
                      <Translate code={img[1]} /> &nbsp;
                    </>
                  ))}
                </div>
              </>
            );
          })}
        </div>
      </>
    );
  } else if (monkReportStatus.status === 'minimumPictures' && typeof monkReportData === 'object') {
    autobizAi = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  } else if (monkReportStatus.status === 'unknowError' && typeof monkReportData === 'object') {
    autobizAi = <span className="status-error">{t('apiErrorAiReport')}</span>;
  } else {
    autobizAi = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  }

  let autobizVinVersions = '';
  if (enableVinDecodingForUser && ((versionType === 'vin' && versions?.length > 0) || vinVersions)) {
    autobizVinVersions = (
      <div className="autobiz-ai-bloc overflow-auto mt-4 accordion mb-4">
        <div
          className="accordion-header"
          role="button"
          onClick={() => setVinVersionsOpen((perv) => !perv)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setVinVersionsOpen((perv) => !perv);
            }
          }}
          tabIndex={0}
        >
          <Label className="text-primary font-weight-bold text-left" style={{ cursor: 'pointer' }}>
            <FontAwesomeIcon style={{ width: 30 }} icon={vinVersionsOpen ? faChevronDown : faChevronRight} />
            {t('vin_decoding')}
          </Label>
        </div>
        <VinDecoding
          vinVersions={vinVersions}
          checkedVinVersions={checkedVinVersions}
          vinVersionsOpen={vinVersionsOpen}
          versionType={versionType}
          versions={versions}
          currentVersion={currentVersion}
          versionSupplier={versionSupplier}
          versionOrg={versionOrg}
          carEquipment={carEquipment}
        />
      </div>
    );
  }

  let caNoaSubTitle = '';
  if (
    caMonkReportStatus.status === 'success' &&
    typeof caMonkReportData === 'object' &&
    monkReportStatus.status === 'success' &&
    typeof monkReportData === 'object'
  ) {
    caNoaSubTitle = (
      <>
        <span style={{ paddingLeft: 30 }}>
          {t('priceGapNoaBuyerCounterAppraisal')}: {currencySign(Math.abs(gaps.autobizFrevosGap))}
        </span>
        <br />
        <span style={{ paddingLeft: 30 }}>
          {t('priceGapNoaNoaCounterAppraisal')}: {currencySign(Math.abs(gaps.monkFrevosGap))}
        </span>
      </>
    );
  } else if (caMonkReportStatus.status === 'success' && typeof caMonkReportData === 'object') {
    caNoaSubTitle = (
      <>
        <span style={{ paddingLeft: 30 }}>
          {t('priceGapNoa')}: {currencySign(Math.abs(caMonkReportData.autobizFrevos - caMonkReportData.monkFrevos))}
        </span>
      </>
    );
  } else if (caMonkReportStatus.status === 'duplicatedData' && typeof caMonkReportData === 'object') {
    caNoaSubTitle = (
      <>
        <span className="status-error" style={{ paddingLeft: 30 }}>
          <Translate code="aiReportErrorPhotos" />
        </span>
      </>
    );
  } else if (caMonkReportStatus.status === 'minimumPictures' && typeof caMonkReportData === 'object') {
    caNoaSubTitle = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  } else if (caMonkReportStatus.status === 'unknowError' && typeof caMonkReportData === 'object') {
    caNoaSubTitle = <span className="status-error">{t('apiErrorAiReport')}</span>;
  } else {
    caNoaSubTitle = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  }

  if (counterInspectionVersion > 0 && counterInspectionVersion > counterInspectionMonkVersion && aiReport) {
    if (!automaticNoa) {
      caAutobizAi = (
        <button
          type="button"
          className="btn btn-block btn-1 mb-1 custom-padding w-auto noa-button"
          onClick={handleMonkReport}
        >
          <img src={logoNoa} alt="logo noa" style={{ marginRight: '8px' }} />
          <span className="d-inline-block d-sm-none d-lg-inline-block px-2">{t('ctaRequestCounterAppraisalNoa')} </span>
          {packageNoaLoading && <Spinner color="light" type="grow" size="sm" />}
        </button>
      );
    }
  } else if (caMonkReportStatus.status === 'pending') {
    caAutobizAi = (
      <span className="d-inline-block d-sm-none d-lg-inline-block text-waiting">{t('aiReportOngoing')}</span>
    );
  } else if (
    caMonkReportStatus.status === 'success' &&
    typeof caMonkReportData === 'object' &&
    monkReportStatus.status === 'success' &&
    typeof monkReportData === 'object'
  ) {
    caAutobizAi = (
      <>
        <Table className="table-responsive mb-0">
          <thead className="offer-table-header">
            <tr>
              <th>
                <></>
              </th>
              <th className="font-weight-normal border-right border-#dee2e6">{t('appraisal')}</th>
              <th className="font-weight-normal border-right border-#dee2e6">{t('counter_appraisal')}</th>
              <th className="font-weight-normal">{t('Gap')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="d-flex" style={{ alignItems: 'center' }}>
                  <span className="text-secondary mr-2">{t('trustScore')}</span>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    size="1x"
                    id="toolTipTrustIndexInformationBox"
                    className="info-icon"
                    onClick={() => setTooltipOpenFirst((prev) => !prev)}
                  />
                  <Tooltip
                    placement="bottom"
                    isOpen={tooltipOpenFirst}
                    target="toolTipTrustIndexInformationBox"
                    toggle={() => !isMobile && setTooltipOpenFirst((prev) => !prev)}
                  >
                    {t('trustIndexInformationBox')}
                  </Tooltip>
                </div>
              </td>
              <td className="border-right border-#dee2e6 text-secondary">{monkReportData.monkCompliances}</td>
              <td className="border-right border-#dee2e6 text-secondary">{caMonkReportData.monkCompliances}</td>
              <td className="text-secondary">{gaps.monkCompliancesGap}</td>
            </tr>
            <tr>
              <td>
                <div className="d-flex" style={{ alignItems: 'center' }}>
                  <span className="numberDamagedPart mr-2">{t('numberDamagedPart')}</span>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    size="1x"
                    id="toolTipPartCountAIInformationBox"
                    className="info-icon"
                    onClick={() => setTooltipOpenSecond((prev) => !prev)}
                  />
                </div>
                <Tooltip
                  placement="bottom"
                  isOpen={tooltipOpenSecond}
                  target="toolTipPartCountAIInformationBox"
                  toggle={() => !isMobile && setTooltipOpenSecond((prev) => !prev)}
                >
                  {t('toolTipPartCountAIInformationBox')}
                </Tooltip>
              </td>
              <td className="border-right border-#dee2e6">
                <></>
              </td>
              <td className="border-right border-#dee2e6">
                <></>
              </td>
              <td>
                <></>
              </td>
            </tr>
            <tr className="delete-interline">
              <td className="font-weight-normal">{t('buyer')}</td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{monkReportData.autobizDamages}</td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{caMonkReportData.autobizDamages}</td>
              <td className="text-secondary">{gaps.autobizDamagesGap}</td>
            </tr>
            <tr className="delete-upper-interline">
              <td className="autobizAi font-weight-normal noa-color">{t('titleAi')}</td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{monkReportData.monkDamages}</td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{caMonkReportData.monkDamages}</td>
              <td className="text-secondary">{gaps.monkDamagesGap}</td>
            </tr>
            <tr>
              <td>
                <span className="bodyRefurbishmentPricing">{t('bodyRefurbishmentPricing')}</span>
              </td>
              <td className="border-right border-#dee2e6">
                <></>
              </td>
              <td className="border-right border-#dee2e6">
                <></>
              </td>
              <td>
                <></>
              </td>
            </tr>
            <tr className="delete-interline">
              <td className="font-weight-normal">{t('buyer')}</td>
              <td className="border-right border-#dee2e6 text-secondary">
                {currencySign(monkReportData.autobizFrevos)}
              </td>
              <td className="border-right border-#dee2e6 text-secondary">
                {currencySign(caMonkReportData.autobizFrevos)}
              </td>
              <td className="text-secondary">{renderFormattedGap(formatGap(gaps.autobizFrevosGap))}</td>
            </tr>
            <tr className="delete-upper-interline">
              <td className="autobizAi font-weight-normal noa-color">{t('titleAi')}</td>
              <td className="border-right border-#dee2e6 text-secondary">{currencySign(monkReportData.monkFrevos)}</td>
              <td className="border-right border-#dee2e6 text-secondary">
                {currencySign(caMonkReportData.monkFrevos)}
              </td>
              <td className="text-secondary">{renderFormattedGap(formatGap(gaps.monkFrevosGap))}</td>
            </tr>
          </tbody>
        </Table>
        <div className="d-flex justify-content-center align-center  mt-5 analysis-tab-gap w-75 btn-noa">
          <div className="w-30  d-lg-inline">
            <a
              target="_blank"
              href={monkReportData.urlReport}
              className="download-btn custom-padding noa-color noa-ctas"
              rel="noopener noreferrer"
              id="theHrefMonkReport"
            >
              <span className="d-inline-block d-lg-inline-block px-2">{t('ctaAiReportMainAppraisal')}</span>
            </a>
          </div>
          <div className="w-30 d-lg-inline">
            <a
              target="_blank"
              href={caMonkReportData.urlReport}
              className="custom-padding download-btn noa-color noa-ctas"
              rel="noopener noreferrer"
              id="theHrefCAMonkReport"
            >
              <span className="d-inline-block d-lg-inline-block px-2">{t('ctaAiReportCounterAppraisal')}</span>
            </a>
          </div>
        </div>
      </>
    );
  } else if (caMonkReportStatus.status === 'success' && typeof caMonkReportData === 'object') {
    caAutobizAi = (
      <>
        <span className="trustScore mr-3">
          {t('trustScore')} {caMonkReportData.monkCompliances}
        </span>
        <FontAwesomeIcon
          icon={faInfoCircle}
          size="1x"
          id="toolTipTrustIndexInformationBox"
          className="info-icon position-absolute ml-n2 mt-1"
          onClick={() => setTooltipOpenFirst((prev) => !prev)}
        />
        <Tooltip
          placement="bottom"
          isOpen={tooltipOpenFirst}
          target="toolTipTrustIndexInformationBox"
          toggle={() => !isMobile && setTooltipOpenFirst((prev) => !prev)}
        >
          {t('trustIndexInformationBox')}
        </Tooltip>
        <Table className="mb-0">
          <thead className="offer-table-header">
            <tr>
              <th>
                <></>
              </th>
              <th className="font-weight-normal border-right border-#dee2e6">{t('buyer')}</th>
              <th className="autobizAi font-weight-normal noa-color">{t('titleAi')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="d-flex">
                  <span className="numberDamagedPart">{t('numberDamagedPart')}</span>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    size="1x"
                    id="toolTipPartCountAIInformationBox"
                    className="info-icon position-absolute"
                    onClick={() => setTooltipOpenSecond((prev) => !prev)}
                  />
                </div>
                <Tooltip
                  placement="bottom"
                  isOpen={tooltipOpenSecond}
                  target="toolTipPartCountAIInformationBox"
                  toggle={() => !isMobile && setTooltipOpenSecond((prev) => !prev)}
                >
                  {t('toolTipPartCountAIInformationBox')}
                </Tooltip>
              </td>
              <td className={`${classDamages} border-right border-#dee2e6`}>{caMonkReportData.autobizDamages}</td>
              <td className={classDamages}>{caMonkReportData.monkDamages}</td>
            </tr>
            <tr>
              <td>
                <span className="bodyRefurbishmentPricing">{t('bodyRefurbishmentPricing')}</span>
              </td>
              <td className="font-weight-bold border-right border-#dee2e6 text-lightgray">
                {currencySign(caMonkReportData.autobizFrevos)}
              </td>
              <td className="font-weight-bold text-lightgray">{currencySign(caMonkReportData.monkFrevos)}</td>
            </tr>
            <tr>
              <td colSpan={3}>
                <a
                  target="_blank"
                  href={caMonkReportData.urlReport}
                  className="custom-padding download-btn d-lg-inline noa-color"
                  rel="noopener noreferrer"
                  id="theHrefCAMonkReport"
                >
                  <span className="d-inline-block d-sm-none d-lg-inline-block noa-color">
                    {t('ctaAiReportCounterAppraisal')}
                  </span>
                </a>
              </td>
            </tr>
          </tbody>
        </Table>
      </>
    );
  } else if (caMonkReportStatus.status === 'duplicatedData' && typeof caMonkReportData === 'object') {
    const duplicationErrors = Object.entries(caMonkReportStatus.comment);
    caAutobizAi = (
      <>
        <div>
          <span className="status-error" style={{ paddingLeft: 5 }}>
            <Translate code="aiReportErrorPhotos" />
          </span>
          {duplicationErrors.map((duplicatedImage) => {
            const duplicatedImages = Object.entries(duplicatedImage[1]);
            return (
              <>
                <div className="status-error">
                  &nbsp;
                  <Translate code={duplicatedImage[0]} />
                  &nbsp;
                  <Translate code="photosSimilar" /> : &nbsp;
                  {duplicatedImages.map((img) => (
                    <>
                      <Translate code={img[1]} /> &nbsp;
                    </>
                  ))}
                </div>
              </>
            );
          })}
        </div>
      </>
    );
  } else if (caMonkReportStatus.status === 'minimumPictures' && typeof caMonkReportData === 'object') {
    caAutobizAi = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  } else if (caMonkReportStatus.status === 'unknowError' && typeof caMonkReportData === 'object') {
    caAutobizAi = <span className="status-error">{t('apiErrorAiReport')}</span>;
  } else {
    caAutobizAi = <span className="status-error">{t('apiErrorAiReportNumberPhotos')}</span>;
  }
  return (
    <>
      <div className="pricing-bloc">
        <div className="left-bloc">
          <Row>
            <Col>
              <label className="title">{t('pricing')}</label>
              <Label className="alternate" for="entryStockDays">
                {t('entryStockDays')}
              </Label>
              <Input
                type="select"
                name="entryStockDays"
                id="entryStockDays"
                onChange={(e) => onChange(e)}
                className="rounded"
                value={entryStockSelectedKey}
              >
                {supervisorEntryDate !== false &&
                  processDate(supervisorEntryDate) > processDate(moment(currentDate).format('DD/MM/YYYY')) &&
                  entryStockSelectedKey !== supervisorEntryDate && (
                    <option value={supervisorEntryStock}>{supervisorEntryDate}</option>
                  )}
                {entryStockOptions &&
                  formatEntryStocksDays(entryStockOptions).map((i, k) => (
                    <option key={i} value={k}>
                      {i}
                    </option>
                  ))}
              </Input>
              {supervisorEntryDate && (
                <small className="info-text-2 mt-n2">{`${t('entryStockDaysBuyer')} : ${supervisorEntryDate}`}</small>
              )}
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <Label for="authorizationToBuy" className="text-secondary text-uppercase font-weight-light text-left">
                {t('car_profile')}
              </Label>
              <Input
                type="select"
                className="rounded"
                id="authorizationToBuy"
                onChange={(e) => {
                  if (e.target.value === 0) setCarProfile(null);
                  setCarProfile(e.target.value);
                }}
                value={carProfile.toUpperCase()}
                disabled={readOnly}
              >
                <option key={0} value={0}>
                  {t('select')}
                </option>
                {sourceProfileLabels.map((obj) => (
                  <option value={obj.toUpperCase()} key={obj.toLowerCase()}>
                    {t(obj.toUpperCase())}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <Calculate readOnly={readOnly} monkFrevos={monkReportData?.monkFrevos} />
              <Calculation
                readOnly={readOnly}
                toggle={toggle}
                coverage={coverage}
                permissionFord={permissionFord}
                originId={originId}
              />
            </Col>
          </Row>
        </div>
        <div className="right-bloc">
          {autobizVinVersions}
          {aiReport && recordStatus !== 40 && (
            <>
              {((inspectionVersion > 0 && inspectionVersion > monkInspectionVersion) ||
                (monkReportStatus.status !== 'success' && monkReportStatus.status !== 'duplicatedData')) && (
                <div className="autobiz-ai-bloc">
                  <Row>
                    <Col>
                      <Label
                        className="text-primary font-weight-bold text-left"
                        style={{
                          display:
                            inspectionVersion > 0 && inspectionVersion > monkInspectionVersion ? 'none' : 'block',
                        }}
                      >
                        <img src={titleNoa} alt="title noa" style={{ marginBottom: '10px' }} />
                      </Label>
                    </Col>
                    {isLoading && (
                      <Col className="text-left">
                        <Spinner color="primary" />
                      </Col>
                    )}
                    <Col xs={12} sm={12} md={12} lg={12}>
                      {autobizAi}
                    </Col>
                  </Row>
                </div>
              )}
              {(monkReportStatus.status === 'success' || monkReportStatus.status === 'duplicatedData') &&
                !(inspectionVersion > 0 && inspectionVersion > monkInspectionVersion) && (
                  <div className="autobiz-ai-bloc accordion">
                    <div
                      className="accordion-header"
                      role="button"
                      onClick={() => toggleAccordion()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          toggleAccordion();
                        }
                      }}
                      tabIndex={0}
                    >
                      <Label className="text-primary font-weight-bold text-left">
                        <FontAwesomeIcon style={{ width: 30 }} icon={open ? faChevronDown : faChevronRight} />
                        <img src={titleNoa} alt="title noa" style={{ marginBottom: '10px' }} />
                      </Label>
                      {!open ? noaSubTitle : ''}
                    </div>
                    <div className="accordion-body" style={{ display: open ? 'block' : 'none' }}>
                      {autobizAi}
                    </div>
                  </div>
                )}
            </>
          )}
          {aiReport && recordStatus === 40 && (
            <>
              {((counterInspectionVersion > 0 && counterInspectionVersion > counterInspectionMonkVersion) ||
                (caMonkReportStatus.status !== 'success' && caMonkReportStatus.status !== 'duplicatedData')) && (
                <div className="autobiz-ai-bloc">
                  <Row>
                    <Col>
                      <Label
                        className="text-primary font-weight-bold text-left"
                        style={{
                          display:
                            counterInspectionVersion > 0 && counterInspectionVersion > counterInspectionMonkVersion
                              ? 'none'
                              : 'block',
                        }}
                      >
                        <img src={titleNoa} alt="title noa" style={{ marginBottom: '10px' }} />
                      </Label>
                    </Col>
                    {isLoading && (
                      <Col className="text-left">
                        <Spinner color="primary" />
                      </Col>
                    )}
                    <Col xs={12} sm={12} md={12} lg={12}>
                      {caAutobizAi}
                    </Col>
                  </Row>
                </div>
              )}
              {(caMonkReportStatus.status === 'success' || caMonkReportStatus.status === 'duplicatedData') &&
                !(counterInspectionVersion > 0 && counterInspectionVersion > counterInspectionMonkVersion) && (
                  <div className="autobiz-ai-bloc accordion">
                    <div
                      className="accordion-header"
                      role="button"
                      onClick={() => toggleAccordion()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          toggleAccordion();
                        }
                      }}
                      tabIndex={0}
                    >
                      <Label className="text-primary font-weight-bold text-left">
                        <FontAwesomeIcon style={{ width: 30 }} icon={open ? faChevronDown : faChevronRight} />
                        <img src={titleNoa} alt="title noa" style={{ marginBottom: '10px' }} />
                      </Label>
                      {!open ? caNoaSubTitle : ''}
                    </div>
                    <div className="accordion-body" style={{ display: open ? 'block' : 'none' }}>
                      {caAutobizAi}
                    </div>
                  </div>
                )}
            </>
          )}
          {data && permissionFord && (
            <div className="gaurantie-bloc">
              <Label className="text-primary font-weight-bold text-left mb-3">
                {`${t('guaranteedPriceInfo')} (${t('guaranteedNumber')} ${data.reference} ${t('applied')}
                ${translateDate(data.createdAt, 'DD.MM.YYYY')})`}
              </Label>
              <Row>
                <Col xs="6" className="label">
                  {t('guaranteedPriceOnline')}
                </Col>
                <Col xs="6" className="value">
                  {data.coveragePrice ? <span>{currencySign(numStr(data.coveragePrice))}</span> : t('inProgress')}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="6" className="label">
                  {t('validityGuaranteedPriceOnline')}
                </Col>
                <Col xs="6" className="value">
                  {(data.coverageDate &&
                    `${t('coverageUntil')} ${translateDate(data.coverageDate, 'DD.MM.YYYY')} ${t('à')} ${moment(
                      data.coverageDate,
                    ).format('HH:mm')}`) ||
                    t('inProgress')}
                </Col>
              </Row>
              <Row>
                <Col xs="6" className="label">
                  {t('guaranteedPriceAcceptance')}
                </Col>
                <Col xs="6" className="value">
                  {(data.authorization && t(data.authorization)) || t('inProgress')}
                </Col>
              </Row>
              <Row className="mb-2">
                <>
                  <Col xs="6" className="label">
                    {t('validityGuaranteedPriceAutobiz')}
                  </Col>
                  <Col xs="6" className="value">
                    {data.showEndValidateDate
                      ? translateDate(data.endValidateDate, 'DD.MM.YYYY')
                      : (data.authorization === 'guaranteedPriceRefused' && t('notValid')) || t('inProgress')}
                  </Col>
                </>
              </Row>
              <Row>
                <Col xs="6" className="label">
                  {t('priceAfterInspection')}
                </Col>
                <Col xs="6" className="value">
                  {data.priceAfterInspection ? <span>{currencySign(data.priceAfterInspection)}</span> : t('inProgress')}
                </Col>
              </Row>
              <Row>
                <Col xs="6" className="label">
                  {t('autobizComment')}
                </Col>
                <Col xs="6" className="value">
                  {(data.comment && data.comment) || t('inProgress')}
                </Col>
              </Row>
            </div>
          )}
          <CounterAppraisalAnalysis />
        </div>
      </div>
      <Model
        isLoading={isLoading}
        isOpen={isOpen}
        toggle={toggle}
        modelData={modelDataPackageNoa || modelData || {}}
        message={message}
        packageNoaMessage={packageNoaMessage}
        reference={reference}
      />
    </>
  );
};

PricingBloc.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  vinVersions: PropTypes.oneOfType([PropTypes.array]),
  checkedVinVersions: PropTypes.func.isRequired,
  versions: PropTypes.oneOfType([PropTypes.array]).isRequired,
  versionType: PropTypes.string.isRequired,
  currentVersion: PropTypes.number.isRequired,
  enableVinDecodingForUser: PropTypes.bool,
  versionSupplier: PropTypes.string,
  versionOrg: PropTypes.string,
  carEquipment: PropTypes.oneOfType([PropTypes.array]).isRequired,
};

PricingBloc.defaultProps = {
  vinVersions: null,
  enableVinDecodingForUser: false,
  versionSupplier: null,
  versionOrg: null,
};

const Model = ({ isLoading, toggle, isOpen, modelData, message, reference, packageNoaMessage }) => {
  const {
    title,
    body,
    footer: { className, btn, btn2 },
    closeIcon,
    type,
  } = modelData;

  const { status, data } = message;
  const classNameBody = modelData?.body?.className || 'remove-padding';

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      {closeIcon && <ModalHeader toggle={toggle} />}
      <ModalBody className={classNameBody}>
        <div className="justify-content-center">
          {type !== 'noaPackage' && (
            <>
              <div className="pb-3">{title && title}</div>
              {(body?.input && (
                <Input
                  type="text"
                  className="form-control rounded"
                  id="reference"
                  value={reference}
                  onChange={body?.onChange}
                />
              )) ||
                body?.message2}
              <span className="ml-2 text-danger">{(status === 406 && t(data?.message)) || ''}</span>
            </>
          )}
          {packageNoaMessage?.length > 0 && packageNoaMessage?.map((el) => <p>{el}</p>)}
        </div>
      </ModalBody>
      <ModalFooter className={className}>
        {btn && (
          <button type="button" className="btn btn-3" onClick={btn.onClick}>
            {btn.name}
          </button>
        )}
        {btn2 && (
          <button type="button" className="btn btn-1" onClick={btn2.onClick} disabled={!btn2?.notDisable && !reference}>
            {btn2.id === 'validate' && isLoading && <Spinner size="sm" />} {btn2.name}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

Model.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  modelData: PropTypes.shape().isRequired,
  message: PropTypes.shape().isRequired,
  reference: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  packageNoaMessage: PropTypes.string.isRequired,
};

Model.defaultProps = {
  reference: '',
};

export default PricingBloc;
